import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiData, SaveDraftClickBiEvent } from '@wix/da-bi/pkg/events';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import {
  getDeviationResourceType,
  getDeviationType,
} from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import HookFormDevtools from '@wix/da-shared-react/pkg/utils/hookform/Devtools';
import DesktopImageSubmitForm from '../../layouts/desktop/ImageSubmitForm';
import DesktopFileSubmitForm from '../../layouts/desktop/FileSubmitForm';
import DesktopLiteratureSubmitForm from '../../layouts/desktop/LiteratureSubmitForm';
import DesktopApplicationForm from '../../layouts/desktop/ApplicationSubmitForm';
import DesktopOriginalsDeliveryForm from '../../layouts/desktop/OriginalsDeliveryForm';
import MobileImageSubmitForm from '../../layouts/mobile/ImageSubmitForm';
import MobileFileSubmitForm from '../../layouts/mobile/FileSubmitForm';
import MobileLiteratureSubmitForm from '../../layouts/mobile/LiteratureSubmitForm';
import MobileApplicationForm from '../../layouts/mobile/ApplicationSubmitForm';
import DAProtectNotice from '../../layouts/common/DAProtectNotice';
import SubmitButton from '../../layouts/common/SubmitButton';
import { useSubmitForm } from '../../hooks/useSubmitForm';
import { useSubmitFormStatus } from '../../hooks/useSubmitFormStatus';
import { SubmitFormContextValue, SubmitFormProvider } from '../../context';
import { SubmitFormParams } from '../../types';

import s from './DeviationFormPopup.scss';

export interface Props {
  params: SubmitFormParams;
  scheduleTimestamp?: string;
  redirectOnSubmit?: boolean;
  requestClose: () => void;
}

const submitFormForDesktop = (
  initResponse: SubmitFormContextValue['initResponse']
) => {
  const deviation = initResponse?.deviation;
  if (initResponse?.requiresPreview) {
    return <DesktopFileSubmitForm />;
  } else if (initResponse?.app) {
    return <DesktopApplicationForm />;
  } else if (deviation?.isOriginalsDelivery) {
    return <DesktopOriginalsDeliveryForm />;
  }

  switch (getDeviationType(deviation)) {
    case DeviationTypes.LITERATURE:
      return <DesktopLiteratureSubmitForm />;
    case DeviationTypes.FILM:
      return <DesktopFileSubmitForm />;
    default:
      return <DesktopImageSubmitForm />;
  }
};

const submitFormForMobile = (
  initResponse: SubmitFormContextValue['initResponse']
) => {
  const deviation = initResponse?.deviation;

  if (initResponse?.requiresPreview) {
    return <MobileFileSubmitForm />;
  }
  if (initResponse?.app) {
    return <MobileApplicationForm />;
  }

  switch (getDeviationType(deviation)) {
    case DeviationTypes.LITERATURE:
      return <MobileLiteratureSubmitForm />;
    case DeviationTypes.FILM:
      return <MobileFileSubmitForm />;
    default:
      return <MobileImageSubmitForm />;
  }
};

const DeviationFormPopup: React.FC<Props> = ({
  params,
  scheduleTimestamp,
  redirectOnSubmit,
  requestClose,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const form = useSubmitForm({
    params,
    requestClose,
  });

  const { deviationId, tierId } = params;
  const {
    formContext,
    isDraft,
    isEdit,
    initResponse,
    requestDeinitializeAndClose,
    requestSafeClose,
    requestSubmit,
    scrollableRef,
  } = form;
  const { isBusy } = useSubmitFormStatus();
  const { watch, control } = formContext;
  const { isInitialized } = watch(['isInitialized']);

  const deviation = initResponse?.deviation;
  const isApp = !!initResponse?.app;
  const isUnpublishedApp = isApp && !deviation?.isPublished;
  const isLiterature =
    deviation && getDeviationType(deviation) === 'literature';
  const isDreamup = deviation?.isDreamsofart;
  const isOriginalsDelivery = deviation?.isOriginalsDelivery;

  const hasSaveAndExitCta =
    isUnpublishedApp || ((isLiterature || isDreamup) && !isEdit);
  const hasSaveAsDraft = !hasSaveAndExitCta && !isEdit && !isApp;
  const hasCancelCta = !hasSaveAsDraft && !hasSaveAndExitCta;

  let modalTitle;
  if (!isInitialized) {
    modalTitle = '';
  } else if (isApp) {
    modalTitle = t('deviationSubmit.title.app');
  } else if (isOriginalsDelivery) {
    modalTitle = t('deviationSubmit.title.originalsDelivery');
  } else if (isDraft) {
    modalTitle = t('deviationSubmit.title.editDraft');
  } else if (isEdit) {
    modalTitle = t('deviationSubmit.title.edit');
  } else if (tierId) {
    modalTitle = t('deviationSubmit.title.createToTier');
  } else {
    modalTitle = t('deviationSubmit.title.create');
  }

  const showDAProtectNotice = useMemo(
    () => !isMobile && deviation?.extended?.isDaProtected,
    [isMobile, deviation]
  );

  const onSubmitSuccess = useCallback(() => {
    requestDeinitializeAndClose();
  }, [requestDeinitializeAndClose]);

  return (
    <SubmitFormProvider {...form}>
      <Modal
        isOpen
        isFullscreen
        heading={modalTitle}
        preventScroll
        footerLeft={
          isInitialized
            ? [showDAProtectNotice && <DAProtectNotice key="da-protect" />]
            : undefined
        }
        footerRight={
          isInitialized
            ? [
                hasCancelCta ? (
                  <TextButton
                    key="footer-cancel"
                    size="large"
                    variant="main"
                    onClick={requestSafeClose}
                  >
                    {t('deviationSubmit.form.button.cancel')}
                  </TextButton>
                ) : null,
                hasSaveAndExitCta ? (
                  <TextButton
                    key="footer-save-exit"
                    size="large"
                    variant="main"
                    biData={BiData<SaveDraftClickBiEvent>({
                      evid: 17,
                      typeid: getDeviationResourceType(deviation),
                      itemid: deviationId,
                    })}
                    onClick={() =>
                      requestSubmit({
                        doPublish: false,
                        doRedirect: isApp ? false : true,
                        onSuccess: isApp ? onSubmitSuccess : undefined,
                      })
                    }
                  >
                    {t('deviationSubmit.form.button.saveAndExit')}
                  </TextButton>
                ) : null,
                hasSaveAsDraft ? (
                  <TextButton
                    key="footer-save"
                    size="large"
                    variant="main"
                    disabled={isBusy}
                    biData={BiData<SaveDraftClickBiEvent>({
                      evid: 17,
                      typeid: getDeviationResourceType(deviation),
                      itemid: deviationId,
                    })}
                    onClick={() =>
                      requestSubmit({
                        doPublish: false,
                        doRedirect: false,
                        onSuccess: onSubmitSuccess,
                      })
                    }
                  >
                    {isOriginalsDelivery
                      ? t(
                          'deviationSubmit.form.button.saveDraft.originalsDelivery'
                        )
                      : t('deviationSubmit.form.button.saveDraft')}
                  </TextButton>
                ) : null,
                <SubmitButton
                  key="footer-submit"
                  scheduleTimestamp={scheduleTimestamp}
                  requestSubmit={() =>
                    requestSubmit({
                      doPublish: true,
                      doRedirect: redirectOnSubmit,
                      onSuccess: !redirectOnSubmit
                        ? onSubmitSuccess
                        : undefined,
                    })
                  }
                  requestSchedule={() =>
                    requestSubmit({
                      doPublish: false,
                      doRedirect: false,
                      onSuccess: onSubmitSuccess,
                    })
                  }
                />,
              ]
            : undefined
        }
        showHeaderBorder
        showFooterBorder
        layoutClassName={s['layout']}
        contentClassName={s['content']}
        footerPadding="horizontally-less"
        onRequestClose={requestSafeClose}
        withScrollableContentWrapper={isMobile}
        bodyScrollLockOptions={{
          allowTouchMove: (el: HTMLElement) => {
            return el.closest('.editor-scrollable') !== null;
          },
        }}
        innerScrollRef={
          isMobile ? el => (scrollableRef.current = el) : undefined
        }
      >
        {!isInitialized && <LoadingIndicator />}
        {isInitialized && !isMobile && submitFormForDesktop(initResponse!)}
        {isInitialized && isMobile && submitFormForMobile(initResponse!)}
        <HookFormDevtools control={control} />
      </Modal>
    </SubmitFormProvider>
  );
};
DeviationFormPopup.displayName = 'DeviationFormPopup';

export default DeviationFormPopup;
